import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.slice.js";
import "core-js/modules/es.object.from-entries.js";
import "core-js/modules/es.object.to-string.js";
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { spacing } from 'styles/globals';
import { pxToRem } from 'utils/ui/units';
import { fillParent } from 'styles/mixins';
import Box from 'components/Box';
import Button from 'components/Button';
import SimplePagination from 'components/SimplePagination';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var Viewport = styled.div.withConfig({
  displayName: "Carousel__Viewport",
  componentId: "sc-ijgrse-0"
})(["overflow:hidden;width:100%;", " ", " border-left:1px solid transparent;"], function (p) {
  return p.$gutterWidth && spacing[p.$gutterWidth] && css(["margin-right:-", ";"], spacing[p.$gutterWidth]);
}, function (p) {
  return p.$lookAhead && spacing[p.$lookAhead] && css(["padding-right:", ";"], spacing[p.$lookAhead]);
});
var Container = styled.div.withConfig({
  displayName: "Carousel__Container",
  componentId: "sc-ijgrse-1"
})(["display:flex;backface-visibility:hidden;touch-action:pan-y;"]);
var Slide = styled.ul.withConfig({
  displayName: "Carousel__Slide",
  componentId: "sc-ijgrse-2"
})(["position:relative;display:flex;flex:0 0 100%;min-width:0;padding:0;margin:0;list-style:none;&:focus,&.focus-visible{outline:none;}&.focus-visible::after{content:'';", " ", ";border:3px solid ", ";border-radius:", ";}"], fillParent, function (p) {
  return p.theme.globals.z.raised;
}, function (p) {
  return p.theme.links;
}, function (p) {
  return pxToRem(p.theme.globals.borderRadius.large);
});
var Item = styled.li.withConfig({
  displayName: "Carousel__Item",
  componentId: "sc-ijgrse-3"
})(["width:", "%;list-style:none;", " ", ""], function (p) {
  return p.$width * 100;
}, function (p) {
  return p.$gutterWidth && spacing[p.$gutterWidth] && css(["padding-right:", ";"], spacing[p.$gutterWidth]);
}, function (p) {
  return p.$verticalSpacing && spacing[p.$verticalSpacing] && css(["padding-top:", ";padding-bottom:", ";"], spacing[p.$verticalSpacing], spacing[p.$verticalSpacing]);
});
var NavButton = styled(Button).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['pos', 'lookAhead'].includes(prop);
  }
}).withConfig({
  displayName: "Carousel__NavButton",
  componentId: "sc-ijgrse-4"
})(["position:absolute;top:35%;z-index:", ";", " ", " &.is-disabled:not(.focus-visible){pointer-events:none;opacity:0;}@media (max-width:", "){display:none;}"], function (p) {
  return p.theme.globals.z.raised;
}, function (p) {
  return p.pos === 'left' && css(["left:0;transform:translateX(-50%);"]);
}, function (p) {
  return p.pos === 'right' && css(["right:0;transform:translateX(50%) scale(-1);", ""], p.lookAhead && spacing[p.lookAhead] && css(["margin-right:", ";"], spacing[p.lookAhead]));
}, function (p) {
  return p.theme.globals.breakpoints.s;
});
var LookAheadCover = styled.div.withConfig({
  displayName: "Carousel__LookAheadCover",
  componentId: "sc-ijgrse-5"
})(["position:absolute;top:0;bottom:0;right:0;width:", ";", " cursor:pointer;z-index:2;"], function (p) {
  return spacing[p.$lookAhead] || '0px';
}, function (p) {
  return p.$gutterWidth && spacing[p.$gutterWidth] && css(["margin-right:-", ";"], spacing[p.$gutterWidth]);
});
function splitIntoGroups(groupLength, items) {
  return items.map(function (_, index) {
    return index % groupLength === 0 ? items.slice(index, index + groupLength) : null;
  }).filter(function (item) {
    return item !== null;
  });
}
function Carousel(_ref) {
  var items = _ref.items,
    maxItems = _ref.maxItems,
    itemsPerSlide = _ref.itemsPerSlide,
    gutterWidth = _ref.gutterWidth,
    lookAhead = _ref.lookAhead,
    _ref$keyBy = _ref.keyBy,
    keyBy = _ref$keyBy === void 0 ? 'id' : _ref$keyBy,
    ariaLabelledBy = _ref['aria-labelledby'],
    verticalSpacing = _ref.verticalSpacing,
    children = _ref.children;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var containerRef = useRef(null);
  var _useEmblaCarousel = useEmblaCarousel({
      align: 'start',
      containScroll: 'trimSnaps',
      duration: 35
    }),
    _useEmblaCarousel2 = _slicedToArray(_useEmblaCarousel, 2),
    emblaRef = _useEmblaCarousel2[0],
    emblaApi = _useEmblaCarousel2[1];
  var _useState = useState(0),
    _useState2 = _slicedToArray(_useState, 2),
    currentSlide = _useState2[0],
    setCurrentSlide = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    canScrollPrev = _useState4[0],
    setCanScrollPrev = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    canScrollNext = _useState6[0],
    setCanScrollNext = _useState6[1];
  var limitedItems = maxItems ? items.slice(0, maxItems) : items;
  var segmentedItems = splitIntoGroups(itemsPerSlide, limitedItems);
  var slideCount = segmentedItems.length;
  var onSelect = useCallback(function () {
    if (!emblaApi) return;
    setCurrentSlide(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);
  useEffect(function () {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return function () {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);
  var goToSlide = function goToSlide(index) {
    return emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollTo(index);
  };
  var goToPrevSlide = function goToPrevSlide() {
    return emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollPrev();
  };
  var goToNextSlide = function goToNextSlide() {
    return emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollNext();
  };
  var slideRefs = useRef({});
  // Stable ref-callback map: one memoised callback per slide index prevents
  // React 19 from detaching/reattaching every slide ref on every render.
  var slideRefCallbacks = useMemo(function () {
    return Object.fromEntries(segmentedItems.map(function (_, index) {
      return [index, function (el) {
        slideRefs.current[index] = el;
      }];
    }));
  },
  // Re-create only when slide count changes, not on every render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [segmentedItems.length]);
  var getSlideRef = function getSlideRef(index) {
    return slideRefCallbacks[index];
  };
  var focusElementWithoutScroll = useCallback(function (element) {
    if (!element) return;
    try {
      element.focus({
        preventScroll: true
      });
    } catch (_unused) {
      // Older Safari versions can throw on focus options.
      element.focus();
    }
  }, []);

  // Focus management boundary fix: checks the entire wrapper Box
  // preventScroll stops the browser from calling scrollIntoView on the slide,
  // which would interfere with embla's CSS transform-based scrolling.
  useEffect(function () {
    var _containerRef$current;
    if ((_containerRef$current = containerRef.current) !== null && _containerRef$current !== void 0 && _containerRef$current.contains(document.activeElement)) {
      focusElementWithoutScroll(slideRefs.current[currentSlide]);
    }
  }, [currentSlide, focusElementWithoutScroll]);
  return /*#__PURE__*/_jsxs(Box, {
    ref: containerRef,
    position: "relative",
    role: "group",
    "aria-roledescription": "carousel",
    "aria-labelledby": ariaLabelledBy,
    children: [/*#__PURE__*/_jsx(NavButton, {
      pos: "left",
      round: true,
      icon: "arrow",
      "aria-label": t('pagination_previous'),
      isDisabled: !canScrollPrev,
      onClick: goToPrevSlide
    }), /*#__PURE__*/_jsx(Viewport, {
      ref: emblaRef,
      $gutterWidth: gutterWidth,
      $lookAhead: lookAhead,
      children: /*#__PURE__*/_jsx(Container, {
        children: segmentedItems.map(function (slide, i) {
          return /*#__PURE__*/_jsx(Slide, {
            ref: getSlideRef(i),
            tabIndex: -1,
            role: "group",
            "aria-roledescription": "slide",
            "aria-describedby": ariaLabelledBy,
            "aria-label": t('pagination_current_page_of_total', {
              current: i + 1,
              count: slideCount
            }),
            children: slide.map(function (item, itemIdx) {
              var itemKey = item[keyBy] || "item-".concat(itemIdx);
              return /*#__PURE__*/_jsx(Item, {
                $width: 1 / itemsPerSlide,
                $gutterWidth: gutterWidth,
                $verticalSpacing: verticalSpacing,
                children: children(item, currentSlide === i)
              }, itemKey);
            })
          }, i);
        })
      })
    }), lookAhead && canScrollNext && /*#__PURE__*/_jsx(LookAheadCover, {
      $lookAhead: lookAhead,
      $gutterWidth: gutterWidth,
      onClick: goToNextSlide
    }), /*#__PURE__*/_jsx(NavButton, {
      pos: "right",
      lookAhead: lookAhead,
      round: true,
      icon: "arrow",
      "aria-label": t('pagination_next'),
      isDisabled: !canScrollNext,
      onClick: goToNextSlide
    }), slideCount > 1 && /*#__PURE__*/_jsx(Box, {
      pt: "xxs",
      children: /*#__PURE__*/_jsx(SimplePagination, {
        zeroBased: true,
        pageCount: slideCount,
        currentPage: currentSlide,
        getButtonLabel: function getButtonLabel(count) {
          return t('pagination_current_page', {
            count: count
          });
        },
        onChange: goToSlide
      })
    })]
  });
}
export default Carousel;